/**
 * Cashbox Transactions Dual-Write Test
 *
 * Tests that cashbox transaction operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * CBT1: Create cashbox transaction (add cash)
 * CBT2: Update cashbox transaction
 * CBT3: Delete cashbox transaction
 */

import {
  saveCashboxTransaction,
  deleteCashboxTransaction,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, dbAtom, organizationIdAtom } from '../global/AtomsProvider';
import { getDailyStatID } from './timezoneFunctions';

// Test variation definitions
export const CASHBOX_TRANSACTIONS_TEST_VARIATIONS = [
  { id: 'CBT1', category: 'create', name: 'Create cashbox transaction (deposit)' },
  { id: 'CBT2', category: 'update', name: 'Update cashbox transaction amount' },
  { id: 'CBT3', category: 'delete', name: 'Delete cashbox transaction (withdrawal)' },
];

// Get test categories for UI grouping
export function getCashboxTransactionsTestCategories() {
  return [
    { id: 'create', name: 'Create Cashbox Transaction', variations: ['CBT1'] },
    { id: 'update', name: 'Update Cashbox Transaction', variations: ['CBT2'] },
    { id: 'delete', name: 'Delete Cashbox Transaction', variations: ['CBT3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getCashboxTransactionsTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const db = jotaiStore.get(dbAtom);
  const organizationId = jotaiStore.get(organizationIdAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  return {
    organization,
    store,
    db,
    organizationId,
  };
}

/**
 * Compare cashbox transaction via backend API
 */
async function compareCashboxTransactionViaApi(transactionId, transactionData, log) {
  try {
    const response = await api.get(`/cashbox-transactions/compare/${transactionId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        transactionId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'cashbox_transaction',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'amount', sb: 'amount' },
      { fs: 'type', sb: 'type', transform: (v) => v?.id || v },
    ];

    for (const { fs, sb, transform } of fieldsToCompare) {
      let fsVal = normalizeValue(transactionData?.[fs]);
      if (transform) {
        fsVal = normalizeValue(transform(transactionData?.[fs]));
      }
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    return {
      transactionId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      transactionId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if cashbox transaction was deleted from Supabase
 */
async function checkCashboxTransactionDeleted(transactionId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/cashbox-transactions/compare/${transactionId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single cashbox transaction test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId } = testData;

  let createdRef = null;
  let createdTransactionData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'CBT1': {
        // Create cashbox transaction (deposit)
        const transactionDate = new Date();
        const dailyStatID = getDailyStatID(null, transactionDate) + '-' + store.id;

        const transactionData = {
          type: { id: 'deposit', text: 'Deposit' },
          amount: 500,
          wallet: store.id + '-cash',
          date: transactionDate,
          dailyStatID: dailyStatID,
          store: store.id,
          organizationId,
        };

        log(`Creating cashbox transaction (deposit): ${transactionData.amount}`);
        await saveCashboxTransaction(db, transactionData);

        // Get the created ref from Firestore - use simpler query to avoid composite index requirement
        const snapshot = await db.collection('cashboxTransactions')
          .where('store', '==', store.id)
          .orderBy('date', 'desc')
          .limit(10)
          .get();

        // Find the matching transaction in memory (avoid complex composite index)
        const matchingDoc = snapshot.docs.find(doc => {
          const data = doc.data();
          return data.type?.id === 'deposit' && data.amount === transactionData.amount;
        });

        if (!matchingDoc) {
          result.error = 'Could not find created cashbox transaction';
          break;
        }

        createdRef = matchingDoc.ref;
        createdTransactionData = { ...transactionData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareCashboxTransactionViaApi(createdRef.id, transactionData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test cashbox transaction...');
        await deleteCashboxTransaction(db, createdTransactionData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'CBT2': {
        // Update cashbox transaction test
        const transactionDate = new Date();
        const dailyStatID = getDailyStatID(null, transactionDate) + '-' + store.id;

        const transactionData = {
          type: { id: 'deposit', text: 'Deposit' },
          amount: 300, // initial amount
          wallet: store.id + '-cash',
          date: transactionDate,
          dailyStatID: dailyStatID,
          store: store.id,
          organizationId,
        };

        log('Creating cashbox transaction to update...');
        await saveCashboxTransaction(db, transactionData);

        // Get the created ref from Firestore - use simpler query to avoid composite index requirement
        const snapshot = await db.collection('cashboxTransactions')
          .where('store', '==', store.id)
          .orderBy('date', 'desc')
          .limit(10)
          .get();

        // Find the matching transaction in memory (avoid complex composite index)
        const matchingDoc = snapshot.docs.find(doc => {
          const data = doc.data();
          return data.type?.id === 'deposit' && data.amount === transactionData.amount;
        });

        if (!matchingDoc) {
          result.error = 'Could not find created cashbox transaction';
          break;
        }

        createdRef = matchingDoc.ref;
        createdTransactionData = { ...transactionData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareCashboxTransactionViaApi(createdRef.id, transactionData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Cashbox transaction was not created in Supabase';
          break;
        }

        // Now update via Firestore
        const newAmount = 600;
        log(`Updating cashbox transaction amount to ${newAmount}...`);
        await createdRef.update({ amount: newAmount });

        // Also update via API for Supabase
        await api.patch(`/cashbox-transactions/${createdRef.id}`, { amount: newAmount });

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare updated values
        const updateCheck = await compareCashboxTransactionViaApi(createdRef.id, { ...transactionData, amount: newAmount }, log);
        result.comparisonResult = updateCheck;
        result.passed = updateCheck.foundInSupabase && valuesMatch(updateCheck.supabaseData?.amount, newAmount);

        // Cleanup
        log('Cleaning up test cashbox transaction...');
        await deleteCashboxTransaction(db, { ...createdTransactionData, amount: newAmount });
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'CBT3': {
        // Delete cashbox transaction test
        const transactionDate = new Date();
        const dailyStatID = getDailyStatID(null, transactionDate) + '-' + store.id;

        const transactionData = {
          type: { id: 'withdrawal', text: 'Withdrawal' },
          amount: 150, // amount to extract
          wallet: store.id + '-cash',
          date: transactionDate,
          dailyStatID: dailyStatID,
          store: store.id,
          organizationId,
        };

        log('Creating cashbox transaction to delete...');
        await saveCashboxTransaction(db, transactionData);

        // Get the created ref from Firestore - use simpler query to avoid composite index requirement
        const snapshot = await db.collection('cashboxTransactions')
          .where('store', '==', store.id)
          .orderBy('date', 'desc')
          .limit(10)
          .get();

        // Find the matching transaction in memory (avoid complex composite index)
        const matchingDoc = snapshot.docs.find(doc => {
          const data = doc.data();
          return data.type?.id === 'withdrawal' && data.amount === transactionData.amount;
        });

        if (!matchingDoc) {
          result.error = 'Could not find created cashbox transaction';
          break;
        }

        createdRef = matchingDoc.ref;
        createdTransactionData = { ...transactionData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareCashboxTransactionViaApi(createdRef.id, transactionData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Cashbox transaction was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting cashbox transaction...');
        await deleteCashboxTransaction(db, createdTransactionData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkCashboxTransactionDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          transactionId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRef && createdTransactionData) {
      try {
        await deleteCashboxTransaction(db, createdTransactionData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all cashbox transaction tests
 */
export async function runFullCashboxTransactionsTest(log = console.log, selectedVariations = null) {
  log('Starting cashbox transactions dual-write tests...');

  const testData = getCashboxTransactionsTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('CASHBOX TRANSACTIONS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || CASHBOX_TRANSACTIONS_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  CASHBOX_TRANSACTIONS_TEST_VARIATIONS,
  getCashboxTransactionsTestCategories,
  getCashboxTransactionsTestData,
  runFullCashboxTransactionsTest,
};
